import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import {
  clearStoredSignUpIdentity,
  getStoredSignUpIdentity,
  persistSignUpIdentity,
  SignUpIdentity,
} from '../utils/signUpIdentity';

const belongsToAnotherMember = (
  stored: SignUpIdentity,
  user?: { id?: string; email?: string }
) => {
  if (!user) return false;

  if (stored.userId && user.id) return stored.userId !== user.id;

  if (stored.email && user.email)
    return stored.email.toLowerCase() !== user.email.toLowerCase();

  return false;
};

/**
 * The member's details for the steps that run after account creation.
 *
 * The screens pass them along in the router state, which a refresh or a deep
 * link throws away. This hook remembers whatever the router state brings it
 * and hands it back on the next screen that asks, so the steps stay unaware
 * of each other and can be reordered freely.
 *
 * What the router state carries always wins. The remembered copy lasts as
 * long as the tab, so it can outlive the sign up it came from: when the app
 * knows who is signed in and the copy names someone else, it is dropped,
 * because sending the wrong email does more damage than sending none.
 */
export const useSignUpIdentity = (): SignUpIdentity => {
  const { state } = useLocation();
  const { user } = useAuth();

  const stored = getStoredSignUpIdentity();
  const isStale = belongsToAnotherMember(stored, user);
  const remembered = isStale ? {} : stored;

  const email = state?.email || remembered.email;
  const userId = state?.userId || remembered.userId;
  const firstName = state?.firstName || remembered.firstName;
  const lastName = state?.lastName || remembered.lastName;

  const carriesIdentity = !!(
    state?.email ||
    state?.userId ||
    state?.firstName ||
    state?.lastName
  );

  useEffect(() => {
    if (isStale) clearStoredSignUpIdentity();

    if (carriesIdentity) {
      persistSignUpIdentity({ email, userId, firstName, lastName });
    }
  }, [isStale, carriesIdentity, email, userId, firstName, lastName]);

  return { email, userId, firstName, lastName };
};
